import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppEventBus from '../services/AppEventBus';
import { API_BASE_URL } from '../config';
import JerseySlot from './JerseySlot';
// Camisetas activas
import goesJersey from '../assets/jerseys/Goes.png';
import MalvinJersey from '../assets/jerseys/Malvin.png';
import BiguaJersey from '../assets/jerseys/Bigua.png';

import cordonJersey from '../assets/jerseys/Cordon.png';
import aguadaVerdirrojoJersey from '../assets/jerseys/Aguada.png';
import hebraicaJersey from '../assets/jerseys/Hebriaca.png';
import emptyJersey from '../assets/jerseys/Pisa SuperDt (19).png';
import defensorJersey from '../assets/jerseys/Defensor.png';
import penarolJersey from '../assets/jerseys/Penarol.png';
import unionJersey from '../assets/jerseys/Union.png';
import welcomeJersey from '../assets/jerseys/Welcome.png';
import nacionalJersey from '../assets/jerseys/Nacional.png';
import urundayJersey from '../assets/jerseys/Urunday.png';
import trouvilleJersey from '../assets/jerseys/Pisa SuperDt (28).png';

// 5 titulares: 2 interiores arriba, 3 perimetrales abajo - Cancha vertical
const starters = [
  { label: 'INTERIOR', x: 35, y: 25, positionType: 'Interior' },
  { label: 'INTERIOR', x: 65, y: 25, positionType: 'Interior' },
  { label: 'PERIMETRAL', x: 20, y: 60, positionType: 'Perimetral' },
  { label: 'PERIMETRAL', x: 50, y: 70, positionType: 'Perimetral' },
  { label: 'PERIMETRAL', x: 80, y: 60, positionType: 'Perimetral' },
];

// 5 suplentes: 3 perimetrales, 2 interior
const bench = [
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'INTERIOR', positionType: 'Interior' },
  { label: 'INTERIOR', positionType: 'Interior' },
];

interface Matchday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface Player {
  id: string;
  name: string;
  team: string;
  position: string;
  positionType?: string;
  price: number;
  totalFantasyPoints: number;
  fantasyPoints?: number;
  isCaptain?: boolean;
}

interface TeamSnapshot {
  fantasyTeamId: string;
  matchdayId: string;
  matchdayName: string;
  snapshotDate: string;
  titulares: Player[];
  suplentes: Player[];
  totalPlayers: number;
}

// Función para formatear nombres de equipos para mostrar al usuario
const formatTeamName = (teamName: string) => {
  if (teamName === 'Penarol') return 'Peñarol';
  return teamName;
};

// Función para obtener la camiseta según el equipo
const getJerseyImage = (teamName: string) => {
  // Coincidir exactamente con los nombres de la BD
  switch (teamName) {
    case 'Penarol':
      return penarolJersey;
    case 'Nacional':
      return nacionalJersey;
    case 'Defensor':
      return defensorJersey;
    case 'Bigua':
      return BiguaJersey;
    case 'Malvin':
      return MalvinJersey;
    case 'Aguada':
      return aguadaVerdirrojoJersey;
    case 'Remeros Mercedes':
      return goesJersey;
    case 'Goes':
      return goesJersey;
    case 'Cordon':
      return cordonJersey;
    case 'Hebraica y Macabi':
      return hebraicaJersey;
    case 'Welcome':
      return welcomeJersey;
    case 'Urunday':
      return urundayJersey;
    case 'Trouville':
      return trouvilleJersey;
    case 'Urupan':
      return urundayJersey;
    case 'Union Atletica':
      return unionJersey;
    default:
      return emptyJersey;
  }
};

interface FechasPageProps {
  initialMatchdayId?: string;
  targetTeamId?: string;
  targetTeamName?: string;
  onBack?: () => void;
}

const FechasPage: React.FC<FechasPageProps> = ({ initialMatchdayId, targetTeamId, targetTeamName, onBack }) => {
  const [matchdays, setMatchdays] = useState<Matchday[]>([]);
  const [selectedMatchday, setSelectedMatchday] = useState<string>(initialMatchdayId || '');
  const [teamSnapshot, setTeamSnapshot] = useState<TeamSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [error, setError] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [playerStatsDetail, setPlayerStatsDetail] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isFromTop5, setIsFromTop5] = useState(false);
  const [bestPlayers, setBestPlayers] = useState<any[] | null>(null);
  const [loadingBestPlayers, setLoadingBestPlayers] = useState(false);
  const [showTop5, setShowTop5] = useState(false);

  const token = localStorage.getItem('token');
  // Si hay targetTeamId, usar ese; si no, usar el del localStorage
  const fantasyTeamId = targetTeamId || localStorage.getItem('fantasyTeamId');
  const isViewingOtherTeam = !!targetTeamId;
  
  // Debug de localStorage
  console.log('🔍 localStorage debug:');
  console.log('  - token from localStorage:', localStorage.getItem('token'));
  console.log('  - fantasyTeamId from localStorage:', localStorage.getItem('fantasyTeamId'));
  console.log('  - All localStorage keys:', Object.keys(localStorage));

  // Cargar nombre del equipo
  const fetchTeamName = useCallback(async () => {
    if (!fantasyTeamId || !token) return;
    // Si ya tenemos el nombre del equipo desde props, usarlo
    if (targetTeamName) {
      setTeamName(targetTeamName);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${fantasyTeamId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.name) {
          setTeamName(data.name);
        }
      }
    } catch (error) {
      console.error('Error loading team name:', error);
    }
  }, [fantasyTeamId, token, targetTeamName]);

  // Cargar nombre del equipo
  useEffect(() => {
    fetchTeamName();
  }, [fetchTeamName]);

  // Cargar fechas disponibles
  useEffect(() => {
    const fetchMatchdays = async () => {
      console.log('🔍 Iniciando fetch de fechas...');
      console.log('Token:', token ? 'Presente' : 'Ausente');
      console.log('FantasyTeamId:', fantasyTeamId ? 'Presente' : 'Ausente');
      
      try {
        setLoading(true);
        console.log('📡 Haciendo request a /api/matchdays...');
        
        const response = await fetch(`${API_BASE_URL}/api/matchdays?limit=100`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Error response:', errorText);
          throw new Error(`Error al cargar las fechas: ${response.status}`);
        }

        // Verificar el tipo de contenido antes de parsear JSON
        const contentType = response.headers.get('content-type');
        console.log('📡 Content-Type:', contentType);
        
        if (!contentType || !contentType.includes('application/json')) {
          const responseText = await response.text();
          console.error('❌ Respuesta no es JSON:', responseText.substring(0, 200));
          throw new Error('El servidor no devolvió JSON');
        }

        const data = await response.json();
        console.log('📊 Data completa:', data);
        console.log('📊 Data.data:', data.data);
        
        // El backend devuelve {data: Array, pagination: {...}}
        const allMatchdays = data.data?.data || data.data || [];
        console.log('📅 Todas las fechas:', allMatchdays);
        console.log('📅 Cantidad de fechas:', allMatchdays.length);
        
        // Filtrar fechas con status 'in_progress' o 'completed'
        console.log('📅 Filtrando fechas por status...');
        
        const pastMatchdays = allMatchdays.filter((matchday: Matchday) => {
          const isAvailable = matchday.status === 'in_progress' || matchday.status === 'completed';
          console.log(`📅 ${matchday.name}: status=${matchday.status}, isAvailable=${isAvailable}`);
          return isAvailable;
        });
        
        console.log('📅 Fechas pasadas:', pastMatchdays);
        console.log('📅 Cantidad de fechas pasadas:', pastMatchdays.length);
        
        const availableMatchdays = Array.isArray(pastMatchdays) ? pastMatchdays : [];

        // Ordenar por fecha de fin (más reciente primero)
        availableMatchdays.sort((a: Matchday, b: Matchday) => {
          return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
        });

        console.log('✅ Fechas disponibles después de ordenar:', availableMatchdays);
        console.log('✅ Cantidad final:', availableMatchdays.length);
        
        setMatchdays(availableMatchdays);

        if (availableMatchdays.length > 0) {
          if (initialMatchdayId && availableMatchdays.some((md: Matchday) => md.id === initialMatchdayId)) {
            setSelectedMatchday(initialMatchdayId);
          } else {
            setSelectedMatchday((prev) => prev || availableMatchdays[0].id);
          }
        } else {
          console.log('⚠️ No hay fechas para seleccionar');
        }
      } catch (err) {
        console.error('❌ Error completo:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar las fechas');
      } finally {
        setLoading(false);
        console.log('🏁 Loading terminado');
      }
    };

    if (token && fantasyTeamId) {
      console.log('🚀 Ejecutando fetchMatchdays...');
      fetchMatchdays();
    } else {
      console.log('⚠️ No se ejecuta fetchMatchdays - faltan token o fantasyTeamId');
    }
  }, [token, fantasyTeamId, initialMatchdayId]);

  // Función para obtener los mejores jugadores por categoría
  const fetchBestPlayers = useCallback(async (matchdayId: string) => {
    if (!matchdayId || !token) return;

    try {
      setLoadingBestPlayers(true);
      const response = await fetch(`${API_BASE_URL}/api/matchdays/${matchdayId}/best-players?limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setBestPlayers(data.data);
        }
      } else {
        setBestPlayers(null);
      }
    } catch (error) {
      console.error('Error loading best players:', error);
      setBestPlayers(null);
    } finally {
      setLoadingBestPlayers(false);
    }
  }, [token]);

  // Cargar mejores jugadores cuando cambia la fecha seleccionada
  useEffect(() => {
    if (selectedMatchday) {
      fetchBestPlayers(selectedMatchday);
    }
  }, [selectedMatchday, fetchBestPlayers]);

  // Polling automático si la jornada está en juego
  useEffect(() => {
    if (!selectedMatchday || !token) return;

    // Verificar si la jornada está en juego
    const currentMatchday = matchdays.find(m => m.id === selectedMatchday);
    const isInProgress = currentMatchday?.status === 'in_progress';

    if (!isInProgress) return;

    // Actualizar cada 30 segundos mientras la jornada está en juego
    const interval = setInterval(() => {
      fetchBestPlayers(selectedMatchday);
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [selectedMatchday, matchdays, token, fetchBestPlayers]);

  // Cargar snapshot del equipo para la fecha seleccionada
  useEffect(() => {
    const fetchTeamSnapshot = async () => {
      if (!selectedMatchday || !fantasyTeamId || !token) {
        setTeamSnapshot(null);
        return;
      }

      try {
        setLoadingSnapshot(true);
        setTeamSnapshot(null); // Limpiar snapshot anterior mientras carga
        setError('');
        
        // Obtener snapshot del equipo
        console.log('🔍 Buscando snapshot para:');
        console.log('  - fantasyTeamId:', fantasyTeamId);
        console.log('  - matchdayId:', selectedMatchday);
        console.log('  - fantasyTeamId type:', typeof fantasyTeamId);
        console.log('  - fantasyTeamId length:', fantasyTeamId?.length);
        
        const snapshotResponse = await fetch(`${API_BASE_URL}/api/snapshots/team/${fantasyTeamId}/matchday/${selectedMatchday}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!snapshotResponse.ok) {
          if (snapshotResponse.status === 404) {
            console.log('⚠️ No hay snapshot para esta fecha específica');
            
            // Probar si hay snapshots para esta fecha en general
            try {
              console.log('🔍 Verificando si hay snapshots para esta fecha en general...');
              const allSnapshotsResponse = await fetch(`${API_BASE_URL}/api/snapshots/matchday/${selectedMatchday}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (allSnapshotsResponse.ok) {
                const allSnapshotsData = await allSnapshotsResponse.json();
                console.log('📊 Snapshots para esta fecha:', allSnapshotsData);
              } else {
                console.log('❌ No hay snapshots para esta fecha en general');
              }
            } catch (verifyError) {
              console.log('❌ Error verificando snapshots:', verifyError);
            }
            
            setTeamSnapshot(null);
            setError(''); // Limpiar error anterior
            return;
          }
          const errorText = await snapshotResponse.text();
          console.error('❌ Error en snapshot:', errorText);
          throw new Error(`Error al cargar el equipo de esta fecha: ${snapshotResponse.status}`);
        }

        const snapshotData = await snapshotResponse.json();
        let teamSnapshot = snapshotData.data;

        // Obtener puntos de fantasy para esta fecha
        console.log('🔍 Obteniendo puntos de fantasy para fecha:', selectedMatchday);
        
        // Obtener puntos de fantasy para cada jugador del equipo
        console.log('🔍 Obteniendo puntos de fantasy para cada jugador...');
        
        // Función para obtener puntos de un jugador específico
        const getPlayerFantasyPoints = async (playerId: string, playerName: string) => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/player-stats/by-player-and-matchday?playerId=${playerId}&matchdayId=${selectedMatchday}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                const fantasyPoints = data.data.fantasyPoints || 0;
                console.log(`✅ ${playerName}: ${fantasyPoints} puntos`);
                return fantasyPoints;
              }
            }
            // Si no hay datos o hay error, devolver 0
            return 0;
          } catch (error) {
            // Error de red - devolver 0
            return 0;
          }
        };

        // Obtener puntos para titulares
        for (let i = 0; i < teamSnapshot.titulares.length; i++) {
          const player = teamSnapshot.titulares[i];
          const points = await getPlayerFantasyPoints(player.id, player.name);
          teamSnapshot.titulares[i] = {
            ...player,
            fantasyPoints: points
          };
        }

        // Obtener puntos para suplentes
        for (let i = 0; i < teamSnapshot.suplentes.length; i++) {
          const player = teamSnapshot.suplentes[i];
          const points = await getPlayerFantasyPoints(player.id, player.name);
          teamSnapshot.suplentes[i] = {
            ...player,
            fantasyPoints: points
          };
        }

        setTeamSnapshot(teamSnapshot);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar el equipo');
        setTeamSnapshot(null);
      } finally {
        setLoadingSnapshot(false);
      }
    };

    fetchTeamSnapshot();
  }, [selectedMatchday, fantasyTeamId, token]);

  useEffect(() => {
    const handleFocus = (matchdayId?: string) => {
      if (matchdayId) {
        setSelectedMatchday(matchdayId);
      }
    };

    AppEventBus.on('focus-fechas', handleFocus);
    return () => {
      AppEventBus.off('focus-fechas', handleFocus);
    };
  }, []);

  useEffect(() => {
    if (initialMatchdayId) {
      setSelectedMatchday(initialMatchdayId);
    }
  }, [initialMatchdayId]);

  const handleMatchdayChange = (matchdayId: string) => {
    setSelectedMatchday(matchdayId);
    setShowTop5(false);
  };

  // Función para calcular el desglose de puntos
  const calculatePointsBreakdown = (stats: any) => {
    if (!stats) return [];
    
    const breakdown = [];
    
    // Puntos anotados (1 punto por punto)
    if (stats.points > 0) {
      breakdown.push({
        label: `${stats.points} punto${stats.points !== 1 ? 's' : ''} anotado${stats.points !== 1 ? 's' : ''}`,
        points: stats.points,
        value: stats.points
      });
    }
    
    // Rebotes (1.2 puntos por rebote)
    if (stats.rebounds > 0) {
      const rebPoints = stats.rebounds * 1.2;
      breakdown.push({
        label: `${stats.rebounds} rebote${stats.rebounds !== 1 ? 's' : ''}`,
        points: rebPoints,
        value: stats.rebounds
      });
    }
    
    // Asistencias (1.5 puntos por asistencia)
    if (stats.assists > 0) {
      const astPoints = stats.assists * 1.5;
      breakdown.push({
        label: `${stats.assists} asistencia${stats.assists !== 1 ? 's' : ''}`,
        points: astPoints,
        value: stats.assists
      });
    }
    
    // Tiros de campo anotados (0.5 puntos por tiro)
    if (stats.fieldGoalsMade > 0) {
      const fgPoints = stats.fieldGoalsMade * 0.5;
      breakdown.push({
        label: `${stats.fieldGoalsMade} doble${stats.fieldGoalsMade !== 1 ? 's' : ''} anotado${stats.fieldGoalsMade !== 1 ? 's' : ''}`,
        points: fgPoints,
        value: stats.fieldGoalsMade
      });
    }
    
    // Triples anotados (1.0 punto extra por triple)
    if (stats.threePointsMade > 0) {
      const threePoints = stats.threePointsMade * 1.0;
      breakdown.push({
        label: `${stats.threePointsMade} triple${stats.threePointsMade !== 1 ? 's' : ''} anotado${stats.threePointsMade !== 1 ? 's' : ''}`,
        points: threePoints,
        value: stats.threePointsMade
      });
    }
    
    // Tiros libres anotados (0.3 puntos por tiro libre)
    if (stats.freeThrowsMade > 0) {
      const ftPoints = stats.freeThrowsMade * 0.3;
      breakdown.push({
        label: `${stats.freeThrowsMade} tiro${stats.freeThrowsMade !== 1 ? 's' : ''} libre${stats.freeThrowsMade !== 1 ? 's' : ''} anotado${stats.freeThrowsMade !== 1 ? 's' : ''}`,
        points: ftPoints,
        value: stats.freeThrowsMade
      });
    }
    
    // Penalización por tiros de campo fallados (-0.3 por tiro fallado)
    const fieldGoalsMissed = (stats.fieldGoalsAttempted || 0) - (stats.fieldGoalsMade || 0);
    if (fieldGoalsMissed > 0) {
      const fgMissedPoints = fieldGoalsMissed * -0.3;
      breakdown.push({
        label: `${fieldGoalsMissed} tiro${fieldGoalsMissed !== 1 ? 's' : ''} de campo fallado${fieldGoalsMissed !== 1 ? 's' : ''}`,
        points: fgMissedPoints,
        value: fieldGoalsMissed
      });
    }
    
    // Penalización por tiros libres fallados (-0.2 por libre fallado)
    const freeThrowsMissed = (stats.freeThrowsAttempted || 0) - (stats.freeThrowsMade || 0);
    if (freeThrowsMissed > 0) {
      const ftMissedPoints = freeThrowsMissed * -0.2;
      breakdown.push({
        label: `${freeThrowsMissed} tiro${freeThrowsMissed !== 1 ? 's' : ''} libre${freeThrowsMissed !== 1 ? 's' : ''} fallado${freeThrowsMissed !== 1 ? 's' : ''}`,
        points: ftMissedPoints,
        value: freeThrowsMissed
      });
    }
    
    return breakdown;
  };

  // Mapear jugadores del snapshot a los slots de la cancha
  const assignedPlayers = useMemo(() => {
    if (!teamSnapshot) return {};
    
    const assigned: { [key: string]: Player | null } = {};
    
    // Separar jugadores por rol
    const titulares = [...teamSnapshot.titulares];
    const suplentes = [...teamSnapshot.suplentes];
    
    // Mapear titulares por posición exacta primero
    for (let i = 0; i < starters.length && titulares.length > 0; i++) {
      const slot = starters[i];
      const matchingPlayer = titulares.find((p: Player) => {
        const positionType = p.positionType || (p.position === 'Interior' ? 'Interior' : p.position === 'Perimetral' ? 'Perimetral' : null);
        return positionType === slot.positionType && 
               !Object.values(assigned).some(assigned => assigned?.id === p.id);
      });
      
      if (matchingPlayer) {
        assigned[`starter-${i}`] = matchingPlayer;
      }
    }
    
    // Si quedan titulares sin asignar, llenar slots vacíos
    const unassignedTitulares = titulares.filter((p: Player) => 
      !Object.values(assigned).some(assigned => assigned?.id === p.id)
    );
    
    for (let i = 0; i < starters.length && unassignedTitulares.length > 0; i++) {
      if (!assigned[`starter-${i}`]) {
        const player = unassignedTitulares.shift();
        if (player) {
          assigned[`starter-${i}`] = player;
        }
      }
    }
    
    // Mapear suplentes por posición exacta primero
    for (let i = 0; i < bench.length && suplentes.length > 0; i++) {
      const slot = bench[i];
      const matchingPlayer = suplentes.find((p: Player) => {
        const positionType = p.positionType || (p.position === 'Interior' ? 'Interior' : p.position === 'Perimetral' ? 'Perimetral' : null);
        return positionType === slot.positionType && 
               !Object.values(assigned).some(assigned => assigned?.id === p.id);
      });
      
      if (matchingPlayer) {
        assigned[`bench-${i}`] = matchingPlayer;
      }
    }
    
    // Si quedan suplentes sin asignar, llenar slots vacíos
    const unassignedSuplentes = suplentes.filter((p: Player) => 
      !Object.values(assigned).some(assigned => assigned?.id === p.id)
    );
    
    for (let i = 0; i < bench.length && unassignedSuplentes.length > 0; i++) {
      if (!assigned[`bench-${i}`]) {
        const player = unassignedSuplentes.shift();
        if (player) {
          assigned[`bench-${i}`] = player;
        }
      }
    }
    
    return assigned;
  }, [teamSnapshot]);

  // Obtener el ID del capitán
  const captainId = useMemo(() => {
    if (!teamSnapshot) return null;
    const captain = [...teamSnapshot.titulares, ...teamSnapshot.suplentes].find(p => p.isCaptain);
    return captain ? captain.id.toLowerCase() : null;
  }, [teamSnapshot]);

  // Función para manejar el clic en un jugador
  const handlePlayerClick = async (player: Player) => {
    setIsFromTop5(false); // Marcar que NO viene del top 5
    if (!selectedMatchday || !token) return;
    
    setSelectedPlayer(player);
    setLoadingStats(true);
    setPlayerStatsDetail(null);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/player-stats/by-player-and-matchday?playerId=${player.id}&matchdayId=${selectedMatchday}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setPlayerStatsDetail(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading player stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Debug del estado
  console.log('🔍 Estado actual:');
  console.log('  - loading:', loading);
  console.log('  - error:', error);
  console.log('  - matchdays.length:', matchdays.length);
  console.log('  - selectedMatchday:', selectedMatchday);
  console.log('  - token:', token ? `Presente (${token.substring(0, 20)}...)` : 'Ausente');
  console.log('  - fantasyTeamId:', fantasyTeamId ? `Presente (${fantasyTeamId})` : 'Ausente');

  if (!token || !fantasyTeamId) {
    console.log('❌ No hay token o fantasyTeamId');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Acceso Requerido</h2>
          <p className="text-white/60">Necesitas estar logueado para ver las fechas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="px-3 pt-2 pb-3 -mt-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl px-4 py-4 mb-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {isViewingOtherTeam && onBack && (
            <button
              onClick={onBack}
              className="text-white/60 hover:text-white mb-2 flex items-center text-sm"
            >
              ← Volver
            </button>
          )}
          <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-0.5">FantasyLub</p>
          <h1 className="text-2xl font-black text-white">
            {isViewingOtherTeam ? `Historial de Fechas - ${teamName || targetTeamName || 'Participante'}` : 'Historial de Fechas'}
          </h1>
          <p className="mt-1 text-sm text-white/60">
            {isViewingOtherTeam
              ? `Ve cómo fue el equipo de ${teamName || targetTeamName || 'este participante'} en cada fecha pasada del torneo`
              : 'Ve cómo fue tu equipo en cada fecha pasada del torneo'}
          </p>
        </div>

        {/* Selector de Fecha */}
        <div className="rounded-2xl px-4 py-3 mb-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-bold text-white">Selecciona una Fecha</h2>

            {loading && matchdays.length === 0 ? (
              <div className="flex items-center">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-lub-accent mr-2"></div>
                <span className="text-white/50 text-sm">Cargando...</span>
              </div>
            ) : matchdays.length === 0 ? (
              <span className="text-white/40 text-sm">No hay fechas disponibles</span>
            ) : (
              <select
                value={selectedMatchday || ''}
                onChange={(e) => handleMatchdayChange(e.target.value)}
                className="px-3 py-2 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-lub-accent min-w-[160px]"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <option value="" className="bg-[#0d1b2e]">Selecciona una fecha</option>
                {matchdays.map((matchday) => (
                  <option key={matchday.id} value={matchday.id} className="bg-[#0d1b2e]">
                    {matchday.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Vista del Equipo */}
        {selectedMatchday && (
          <div className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {loadingSnapshot ? (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-lub-accent"></div>
                <p className="text-white/50 mt-2">Cargando equipo...</p>
              </div>
            ) : error ? (
              <div className="rounded-lg px-4 py-3" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)' }}>
                <p className="font-medium text-red-300">Error</p>
                <p className="text-red-200/80 text-sm">{error}</p>
              </div>
            ) : teamSnapshot ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h2 className="text-sm font-bold text-white">{teamName || 'Tu Equipo'} · {teamSnapshot.matchdayName}</h2>
                  </div>
                  {(teamSnapshot.titulares.some(p => p.fantasyPoints !== undefined) || teamSnapshot.suplentes.some(p => p.fantasyPoints !== undefined)) && (
                    <div className="text-right">
                      <p className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Puntos</p>
                      <p className="text-lg font-black" style={{ color: '#4ade80' }}>
                        {Math.round(teamSnapshot.titulares.reduce((sum, p) => sum + (p.fantasyPoints || 0) * (p.isCaptain ? 1.5 : 1.0), 0) +
                          teamSnapshot.suplentes.reduce((sum, p) => sum + (p.fantasyPoints || 0) * 0.5, 0))}
                      </p>
                    </div>
                  )}
                </div>

                {/* Cancha de Basketball */}
                <div className="flex flex-col gap-3">
                  {/* Equipo */}
                  <div className="order-2">
                    <div className="p-2 -mt-4">
                      {/* Cancha de basketball */}
                      <div className="relative mb-2">
                        <div
                          className="relative w-full h-56 sm:h-64 rounded-lg"
                          style={{
                            backgroundImage: `url('/court-bg.png')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center top',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: 'transparent'
                          }}
                        >
                          {/* Slots titulares */}
                          {starters.map((slot, idx) => {
                            const key = `starter-${idx}`;
                            const assignedPlayer = assignedPlayers[key];
                            return (
                              <div
                                key={idx}
                                className="absolute flex flex-col items-center"
                                style={{
                                  left: `${slot.x}%`,
                                  top: `${slot.y}%`,
                                  transform: 'translate(-50%, -50%)'
                                }}
                              >
                                <JerseySlot
                                  player={assignedPlayer ? {
                                    id: parseInt(assignedPlayer.id, 10),
                                    name: assignedPlayer.name,
                                    team: assignedPlayer.team,
                                    price: assignedPlayer.price,
                                    positionType: assignedPlayer.positionType || (assignedPlayer.position === 'Interior' ? 'Interior' : assignedPlayer.position === 'Perimetral' ? 'Perimetral' : undefined),
                                  } : undefined}
                                  isCaptain={captainId === assignedPlayer?.id?.toLowerCase()}
                                  onClick={() => {
                                    if (assignedPlayer) {
                                      handlePlayerClick(assignedPlayer);
                                    }
                                  }}
                                  isStarter={true}
                                  canChangeCaptain={false}
                                  slotPositionType={slot.positionType}
                                  displayPoints={assignedPlayer && assignedPlayer.fantasyPoints !== undefined 
                                    ? Math.round(assignedPlayer.fantasyPoints * (assignedPlayer.isCaptain ? 1.5 : 1.0))
                                    : undefined}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Suplentes */}
                      <div className="rounded-2xl pt-2 pb-2 px-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <h3 className="text-[10px] font-black text-white/50 uppercase tracking-widest mb-1">Suplentes</h3>
                        <div className="flex flex-wrap justify-center gap-2">
                          {bench.map((slot, idx) => {
                            const key = `bench-${idx}`;
                            const assignedPlayer = assignedPlayers[key];
                            return (
                              <div key={idx} className="flex flex-col items-center">
                                <JerseySlot
                                  player={assignedPlayer ? {
                                    id: parseInt(assignedPlayer.id, 10),
                                    name: assignedPlayer.name,
                                    team: assignedPlayer.team,
                                    price: assignedPlayer.price,
                                    positionType: assignedPlayer.positionType || (assignedPlayer.position === 'Interior' ? 'Interior' : assignedPlayer.position === 'Perimetral' ? 'Perimetral' : undefined),
                                  } : undefined}
                                  isCaptain={captainId === assignedPlayer?.id?.toLowerCase()}
                                  onClick={() => {
                                    if (assignedPlayer) {
                                      handlePlayerClick(assignedPlayer);
                                    }
                                  }}
                                  isStarter={false}
                                  canChangeCaptain={false}
                                  size="sm"
                                  slotPositionType={slot.positionType}
                                  displayPoints={assignedPlayer && assignedPlayer.fantasyPoints !== undefined
                                    ? Math.round(assignedPlayer.fantasyPoints * 0.5)
                                    : undefined}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top 5 colapsable */}
                  <div className="order-1">
                    <button
                      type="button"
                      onClick={() => setShowTop5(v => !v)}
                      className="w-full flex items-center justify-between px-4 py-2.5 rounded-2xl transition-colors hover:bg-white/5"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white">Top 5 de la Fecha</span>
                        {loadingBestPlayers && (
                          <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-lub-accent"></div>
                        )}
                        {!loadingBestPlayers && bestPlayers && bestPlayers.length > 0 && !showTop5 && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,184,0,0.15)', color: '#FFB800' }}>
                            #{1} {bestPlayers[0]?.name?.split(' ').pop()} · {bestPlayers[0]?.fantasyPoints} pts
                          </span>
                        )}
                      </div>
                      <svg
                        className={`w-4 h-4 text-white/40 transition-transform duration-200 ${showTop5 ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showTop5 && (
                    <div className="rounded-b-2xl px-4 pb-4 pt-3 -mt-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderTop: 'none' }}>
                      {loadingBestPlayers ? (
                        <div className="text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-lub-accent"></div>
                          <p className="text-sm text-white/50 mt-2">Cargando...</p>
                        </div>
                      ) : bestPlayers && bestPlayers.length > 0 ? (
                        <div className="space-y-2">
                          {bestPlayers.map((player, idx) => (
                            <div
                              key={player.playerId || idx}
                              className="rounded-lg p-2.5 cursor-pointer hover:bg-white/5 transition-colors"
                              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                              onClick={() => {
                                // Cargar estadísticas del jugador
                                const loadPlayerStats = async () => {
                                  try {
                                    setLoadingStats(true);
                                    setIsFromTop5(true); // Marcar que viene del top 5
                                    setSelectedPlayer({
                                      id: player.playerId,
                                      name: player.name,
                                      team: player.team,
                                      position: player.positionType,
                                      price: 0,
                                      totalFantasyPoints: player.fantasyPoints,
                                      fantasyPoints: player.fantasyPoints,
                                      isCaptain: false
                                    });

                                    const response = await fetch(
                                      `${API_BASE_URL}/api/player-stats/by-player-and-matchday?playerId=${player.playerId}&matchdayId=${selectedMatchday}`,
                                      {
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        }
                                      }
                                    );

                                    if (response.ok) {
                                      const data = await response.json();
                                      if (data.success && data.data) {
                                        setPlayerStatsDetail(data.data);
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error loading player stats:', error);
                                  } finally {
                                    setLoadingStats(false);
                                  }
                                };

                                loadPlayerStats();
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: 'rgba(0,87,184,0.25)', color: '#93c5fd' }}>
                                      #{idx + 1}
                                    </span>
                                    <p className="text-sm font-semibold text-white">{player.name}</p>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-xs text-white/50">{formatTeamName(player.team)}</p>
                                    <span className="text-xs text-white/50 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                      {player.category}
                                    </span>
                                    <span className="text-xs text-white/50 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                      {player.positionType}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right ml-2">
                                  <p className="text-sm font-bold" style={{ color: '#4ade80' }}>{player.fantasyPoints} pts</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-white/40 text-center py-4">
                          <p>No hay estadísticas disponibles aún para esta fecha.</p>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-white/20 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No hay datos para esta fecha</h3>
                <p className="text-white/50">No se encontró información del equipo para esta fecha.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de desglose de puntos */}
      {selectedPlayer && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedPlayer(null);
              setPlayerStatsDetail(null);
              setIsFromTop5(false);
            }
          }}
        >
          <div
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{ background: '#0d1b2e', border: '1px solid rgba(255,255,255,0.08)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {loadingStats ? (
              <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-lub-accent"></div>
                <p className="text-white/50 mt-4">Cargando estadísticas...</p>
              </div>
            ) : (
              <>
                {/* Header del modal */}
                <div className="p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Desglose de Puntos</h3>
                    <button
                      onClick={() => {
                        setSelectedPlayer(null);
                        setPlayerStatsDetail(null);
                        setIsFromTop5(false);
                      }}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {selectedPlayer && (
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-14 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: '#0a1628', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <img
                          src={getJerseyImage(selectedPlayer?.team || '')}
                          alt={`Camiseta de ${formatTeamName(selectedPlayer?.team || '')}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{selectedPlayer?.name}</p>
                        <p className="text-sm text-white/50">{formatTeamName(selectedPlayer?.team || '')}</p>
                        <p className="text-xs text-white/40">{selectedPlayer?.position}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contenido del modal */}
                <div className="p-5">
                  {playerStatsDetail && playerStatsDetail.fantasyPoints !== undefined && playerStatsDetail.fantasyPoints > 0 ? (
                    <>
                      {/* Desglose de puntos */}
                      <div className="space-y-1 mb-2">
                        {calculatePointsBreakdown(playerStatsDetail).map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-1.5"
                            style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                          >
                            <span className="text-xs text-white/60">{item.label}</span>
                            <span className={`text-xs font-medium ${item.points >= 0 ? '' : 'text-red-400'}`} style={item.points >= 0 ? { color: '#4ade80' } : undefined}>
                              {`${item.points >= 0 ? '+' : ''}${item.points.toFixed(1)} pts`}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Nota sobre redondeo */}
                      <div className="mb-2 rounded-lg p-2" style={{ background: 'rgba(0,87,184,0.12)', border: '1px solid rgba(0,87,184,0.3)' }}>
                        <p className="text-xs text-blue-200/80 leading-tight">
                          <span className="font-medium text-white">Nota:</span> Los valores se muestran con 1 decimal en el desglose, pero el total final se redondea al entero más cercano (0.5 o más redondea hacia arriba).
                        </p>
                      </div>

                      {/* Total */}
                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        {isFromTop5 ? (
                          // Si viene del top 5, solo mostrar total base
                          <div className="flex items-center justify-between">
                            <span className="text-base font-semibold text-white">Total</span>
                            <span className="text-base font-bold" style={{ color: '#4ade80' }}>
                              {Math.round(playerStatsDetail.fantasyPoints)} pts
                            </span>
                          </div>
                        ) : selectedPlayer?.isCaptain ? (
                          <>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-base font-semibold text-white">Total base</span>
                              <span className="text-base font-semibold text-white/70">
                                {Math.round(playerStatsDetail.fantasyPoints)} pts
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-base font-semibold text-white">Total</span>
                              <span className="text-base font-bold" style={{ color: '#4ade80' }}>
                                {Math.round(playerStatsDetail.fantasyPoints * 1.5)} pts
                              </span>
                            </div>
                            <div className="mt-1.5 text-xs" style={{ color: '#FFB800' }}>
                              <span className="font-medium">Capitán:</span> {Math.round(playerStatsDetail.fantasyPoints)} × 1.5 = {Math.round(playerStatsDetail.fantasyPoints * 1.5)} pts
                            </div>
                          </>
                        ) : selectedPlayer && teamSnapshot && teamSnapshot.suplentes.some(p => p.id === selectedPlayer?.id) ? (
                          <>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-base font-semibold text-white">Total base</span>
                              <span className="text-base font-semibold text-white/70">
                                {Math.round(playerStatsDetail.fantasyPoints)} pts
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-base font-semibold text-white">Total</span>
                              <span className="text-base font-bold" style={{ color: '#4ade80' }}>
                                {Math.round(playerStatsDetail.fantasyPoints * 0.5)} pts
                              </span>
                            </div>
                            <div className="mt-1.5 text-xs text-blue-300">
                              <span className="font-medium">Suplente:</span> {Math.round(playerStatsDetail.fantasyPoints)} × 0.5 = {Math.round(playerStatsDetail.fantasyPoints * 0.5)} pts
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-base font-semibold text-white">Total</span>
                            <span className="text-base font-bold" style={{ color: '#4ade80' }}>
                              {Math.round(playerStatsDetail.fantasyPoints)} pts
                            </span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-white/50">No hay estadísticas disponibles para este jugador en esta fecha.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FechasPage;
